import React, { Fragment } from 'react';
import { Line } from 'react-chartjs-2';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';

import LineChartLegend from './LineChartLegend';

import styles from './index.module.css';

const LineChart = (props) => {
  const { datasets, labels, className, options, width, height, extraLegend } = props;

  return (
    <Fragment>
      {options.showCustomLegend && (
        // Chart Legend
        <div
          className={classNames(
            styles['line-chart-legend'],
            'd-flex',
            'justify-content-md-center',
            'justify-content-start',
            'align-items-center'
          )}
        >
          <ul className={`line-chart-legend-list p-0 m-0 ${datasets.length > 1 ? 'multiple' : ''}`}>
          {extraLegend.map((x) => (
              <LineChartLegend
                key={x}
                label={x}
                backgroundColor={"#eee"}
              />
            ))}
            {datasets.map((dataset) => (
              <LineChartLegend
                key={dataset.datasetKeyProvider}
                label={dataset.label}
                backgroundColor={dataset.pointBackgroundColor}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Chart */}
      <div
        className={classNames(styles[`line-chart-container`], className)}
        style={{ minHeight: height }}
      >
        <Line data={{ labels, datasets }} options={options} width={width} height={height} />
      </div>
    </Fragment>
  );
};

LineChart.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  labels: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  options: PropTypes.shape({
    elements: PropTypes.shape({
      line: PropTypes.shape({
        tension: PropTypes.number,
      }),
    }),
    layout: PropTypes.shape({
      padding: PropTypes.shape({
        bottom: PropTypes.number,
        left: PropTypes.number,
        right: PropTypes.number,
        top: PropTypes.number,
      }),
    }),
    legend: PropTypes.shape({
      display: PropTypes.bool,
    }),
    maintainAspectRatio: PropTypes.bool,
    onResize: PropTypes.func,
    responsive: PropTypes.bool,
    scales: PropTypes.shape({
      xAxes: PropTypes.arrayOf(
        PropTypes.shape({
          gridLines: PropTypes.shape({
            display: PropTypes.bool,
            color: PropTypes.string,
            drawBorder: PropTypes.bool,
            zeroLineColor: PropTypes.string,
          }),
          ticks: PropTypes.shape({
            callback: PropTypes.func,
            fontColor: PropTypes.string,
            fontFamily: PropTypes.string,
            fontSize: PropTypes.number,
            maxTicksLimit: PropTypes.number,
            padding: PropTypes.number,
            xMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          }),
        })
      ),
      yAxes: PropTypes.arrayOf(
        PropTypes.shape({
          gridLines: PropTypes.shape({
            display: PropTypes.bool,
            color: PropTypes.string,
            drawBorder: PropTypes.bool,
            zeroLineColor: PropTypes.string,
          }),
          ticks: PropTypes.shape({
            beginAtZero: PropTypes.bool,
            callback: PropTypes.func,
            fontColor: PropTypes.string,
            fontFamily: PropTypes.string,
            fontSize: PropTypes.number,
            maxTicksLimit: PropTypes.number,
            padding: PropTypes.number,
          }),
        })
      ),
    }),
    showCustomLegend: PropTypes.bool,
    title: PropTypes.shape({
      display: PropTypes.bool,
    }),
    showTooltips: PropTypes.bool,
    tooltips: PropTypes.shape({
      bodyFontSize: PropTypes.number,
      callback: PropTypes.func,
      custom: PropTypes.func,
      enabled: PropTypes.bool,
      intersect: PropTypes.bool,
      mode: PropTypes.string,
      position: PropTypes.string,
      titleFontSize: PropTypes.number,
      xPadding: PropTypes.number,
      yPadding: PropTypes.number,
    }),
  }).isRequired,
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      backgroundColor: PropTypes.string,
      borderColor: PropTypes.string,
      borderWidth: PropTypes.number,
      data: PropTypes.arrayOf(PropTypes.number),
      datasetKeyProvider: PropTypes.string,
      hitRadius: PropTypes.number,
      hoverRadius: PropTypes.number,
      hoverBorderWidth: PropTypes.number,
      label: PropTypes.string,
      pointBackgroundColor: PropTypes.string,
      pointRadius: PropTypes.number,
    })
  ).isRequired,
  className: PropTypes.string,
  extraLegend: PropTypes.array
};

LineChart.defaultProps = {
  className: '',
  extraLegend: []
};

export default LineChart;
